import Navigation from "@/components/Navigation";
import { Card } from "@/components/ui/card";
import { Helmet } from "react-helmet";
import SchemaMarkup from "@/components/SchemaMarkup";
import Breadcrumb from "@/components/Breadcrumb";
import { createArticleSchema } from "@/utils/schemaHelpers";
import HoroscopeContent from "@/components/HoroscopeContent";

const HoroscopoGeminis = () => {
  const articleSchema = createArticleSchema(
    "Géminis: Horóscopo Diario, Características y Compatibilidad con los 12 Signos",
    "Descubre el horóscopo diario, semanal y mensual de Géminis. Predicciones de amor, trabajo, salud y dinero para personas nacidas entre el 21 de mayo y el 20 de junio.",
    `${window.location.origin}/horoscopo-geminis`
  );

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Inicio",
        "item": window.location.origin
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Horóscopo Géminis",
        "item": `${window.location.origin}/horoscopo-geminis`
      }
    ]
  };

  const breadcrumbItems = [
    { name: "Inicio", url: "/" },
    { name: "Horóscopo", url: "/horoscopo" },
    { name: "Géminis" }
  ];

  return (
    <>
      <Helmet>
        <title>Géminis: Horóscopo Diario, Características y Compatibilidad con los 12 Signos</title>
        <meta name="description" content="Descubre el horóscopo diario, semanal y mensual de Géminis. Predicciones de amor, trabajo, salud y dinero para personas nacidas entre el 21 de mayo y el 20 de junio." />
        <link rel="canonical" href={`${window.location.origin}/horoscopo-geminis`} />
        <meta property="og:title" content="Géminis: Horóscopo Diario, Características y Compatibilidad" />
        <meta property="og:description" content="Descubre el horóscopo diario, semanal y mensual de Géminis. Predicciones de amor, trabajo, salud y dinero." />
        <meta property="og:url" content={`${window.location.origin}/horoscopo-geminis`} />
        <meta property="og:type" content="article" />
      </Helmet>
      
      <SchemaMarkup schema={articleSchema} />
      <SchemaMarkup schema={breadcrumbSchema} />
      
      <div className="min-h-screen">
        <Navigation />
        
        <main className="pt-24 pb-12 px-4">
          <div className="container mx-auto max-w-4xl">
            <Breadcrumb items={breadcrumbItems} />
            <div className="text-center mb-12">
              <div className="text-6xl mb-4">♊</div>
              <h1 className="text-2xl font-bold mb-4 text-glow">
                Horóscopo de Géminis
              </h1>
              <p className="text-xl text-muted-foreground">21 de Mayo - 20 de Junio</p>
            </div>

            <section className="mb-16">
              <h2 className="text-2xl font-bold mb-6 text-primary">Tu Horóscopo Géminis: Predicciones Actualizadas</h2>
              <HoroscopeContent sign="geminis" signName="Géminis" />
            </section>

            {/* Deep Characteristics */}
            <section className="mb-16">
              <h2 className="text-2xl font-bold mb-6 text-secondary">Características Profundas del Signo Géminis: El Comunicador</h2>
              
              <Card className="p-8 mb-6 gradient-mystic">
                <h3 className="text-xl font-bold mb-4">Los Pilares Astrológicos de Géminis</h3>
                <div className="grid md:grid-cols-3 gap-6">
                  <div>
                    <h4 className="font-bold text-base mb-2 text-primary">💨 Elemento: Aire</h4>
                    <p className="text-muted-foreground">Enfoque en el intelecto, la comunicación y las ideas. Géminis vive en el mundo del pensamiento y la palabra.</p>
                  </div>
                  <div>
                    <h4 className="font-bold text-base mb-2 text-secondary">🔄 Modalidad: Mutable</h4>
                    <p className="text-muted-foreground">Define la adaptabilidad, la curiosidad y la dualidad. Flexible y versátil en todas las situaciones.</p>
                  </div>
                  <div>
                    <h4 className="font-bold text-base mb-2 text-accent">☿ Planeta Regente: Mercurio</h4>
                    <p className="text-muted-foreground">Dios de la Comunicación y los Mensajeros. Rige el pensamiento, la palabra y el intercambio de información.</p>
                  </div>
                </div>
              </Card>

              <Card className="p-8 mb-6 gradient-celestial">
                <h3 className="text-xl font-bold mb-4">Perfil de Personalidad Géminis: Fortalezas y Debilidades</h3>
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <h4 className="font-bold text-base mb-3 text-primary">✨ Fortalezas</h4>
                    <ul className="space-y-2 text-muted-foreground">
                      <li>• <strong>Inteligencia:</strong> Mente rápida y analítica</li>
                      <li>• <strong>Versatilidad:</strong> Se adapta a cualquier situación</li>
                      <li>• <strong>Curiosidad:</strong> Sed insaciable de conocimiento</li>
                      <li>• <strong>Sociabilidad:</strong> Conecta fácilmente con otros</li>
                      <li>• <strong>Elocuencia:</strong> Maestro de la palabra</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-bold text-base mb-3 text-secondary">⚠️ Debilidades</h4>
                    <ul className="space-y-2 text-muted-foreground">
                      <li>• <strong>Inconstancia:</strong> Dificultad para comprometerse</li>
                      <li>• <strong>Superficialidad:</strong> Puede quedarse en la superficie</li>
                      <li>• <strong>Nerviosismo:</strong> Mente siempre activa</li>
                      <li>• <strong>Dualidad:</strong> Contradicciones internas</li>
                      <li>• <strong>Dispersión:</strong> Muchos intereses, poca profundidad</li>
                    </ul>
                  </div>
                </div>
                <div className="text-center p-4 bg-primary/10 rounded-lg">
                  <p className="text-lg font-bold text-primary">Mantra Clave: "Yo Pienso"</p>
                </div>
              </Card>

              <Card className="p-8 mb-6 gradient-mystic">
                <h3 className="text-xl font-bold mb-4">Géminis en el Amor: Conexión Mental y Libertad</h3>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  Para Géminis, el amor comienza en la mente. Busca una pareja que pueda estimularlo intelectualmente, con quien pueda mantener conversaciones profundas y divertidas. La conexión mental es más importante que la física. Valora la variedad, la espontaneidad y la libertad en las relaciones.
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  Puede tener dificultades con el compromiso a largo plazo si siente que su libertad está amenazada. Necesita una pareja que entienda su necesidad de espacio, variedad y estimulación constante. Cuando encuentra al compañero adecuado, es juguetón, ingenioso y mantiene viva la llama con su capacidad para renovar la relación constantemente.
                </p>
              </Card>

              <Card className="p-8 gradient-celestial">
                <h3 className="text-xl font-bold mb-4">Géminis en el Trabajo y Finanzas: Redes, Ventas e Ideas Brillantes</h3>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  Géminis destaca en carreras que involucran comunicación, escritura, periodismo, ventas, marketing, traducción, enseñanza y tecnología. Su capacidad para procesar información rápidamente y conectar conceptos dispares lo hace invaluable en campos que requieren pensamiento innovador.
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  Con las finanzas, Géminis es ingenioso pero a veces impulsivo. Le atraen las ideas de negocio nuevas y las inversiones tecnológicas. Necesita diversificar sus fuentes de ingresos para mantener su interés. Excelente para generar múltiples flujos de ingreso a través de su versatilidad.
                </p>
              </Card>
            </section>

            {/* Compatibility */}
            <section>
              <h2 className="text-2xl font-bold mb-6 text-accent">Compatibilidad de Géminis con los Otros Signos Zodiacales</h2>
              
              <Card className="p-8 mb-6 gradient-mystic">
                <h3 className="text-xl font-bold mb-4">💚 Tu Match Perfecto</h3>
                <div className="space-y-4">
                  <div>
                    <h4 className="font-bold text-base mb-2">♎ Libra y ♒ Acuario</h4>
                    <p className="text-muted-foreground">
                      Los signos de aire comparten el amor por las ideas, la comunicación y la libertad. Con Libra hay armonía social y apreciación estética mutua. Con Acuario, una conexión mental brillante y visión de futuro compartida.
                    </p>
                  </div>
                  <div>
                    <h4 className="font-bold text-base mb-2">♈ Aries y ♌ Leo</h4>
                    <p className="text-muted-foreground">
                      Los signos de fuego aportan la pasión y la iniciativa que complementan la naturaleza intelectual de Géminis. Aries trae aventura y espontaneidad, mientras que Leo añade creatividad y dramatismo a la relación.
                    </p>
                  </div>
                </div>
              </Card>

              <Card className="p-8 gradient-celestial">
                <h3 className="text-xl font-bold mb-4">⚡ Los Desafíos y Lecciones de Géminis</h3>
                <p className="text-muted-foreground mb-4">
                  La profundidad emocional es el gran desafío de Géminis. Los signos de agua (Cáncer, Escorpio, Piscis) lo empujan a explorar sus sentimientos más allá del análisis intelectual. Estas relaciones pueden ser incómodas pero transformadoras.
                </p>
                <p className="text-muted-foreground">
                  Los signos de tierra (Tauro, Virgo, Capricornio) enseñan a Géminis el valor de la consistencia, la paciencia y el seguimiento. Sagitario, su signo opuesto, lo desafía a encontrar un propósito filosófico más profundo más allá de la mera acumulación de datos.
                </p>
              </Card>
            </section>
          </div>
        </main>
      </div>
    </>
  );
};

export default HoroscopoGeminis;
